import React, { useEffect } from 'react';

import { ChakraProvider } from '@chakra-ui/react';
import { useUnifyIntent } from '@unifygtm/intent-react';
import { observer } from 'mobx-react-lite';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { ErrorBoundary } from '@r-client/republic/feature/errors';
import { useGdprQuery } from '@r-client/republic/feature/global';
import {
  BusinessCases,
  CreateWallet,
  FounderSpotlights,
  InvestmentOpportunities,
  JoinCommunity,
  Opportunities,
  Regulations,
  RepublicNote,
  RepublicNumbers,
  SecondaryMarket,
  Services,
  TopSection,
  WhatsNew,
} from '@r-client/republic/feature/homepage';
import { Main, MainLoggedOut } from '@r-client/republic/feature/layout';
import { MainLayout } from '@r-client/republic/feature/main-layout';
import {
  defaultCoverImage,
  PageSEOMeta,
  usHomeCoverImage,
} from '@r-client/republic/feature/seo';
import { ZendeskScript } from '@r-client/republic/feature/zendesk';
import { republicLegacy } from '@r-client/republic/ui/theme';
import {
  useAnalytics,
  useBackendAnalyticsIntegration,
} from '@r-client/shared/data/analytics';
import { useAuth } from '@r-client/shared/data/auth';
import {
  useRegionContext,
  withRegionContext,
} from '@r-client/shared/feature/region';
import { toAbsoluteURL, useAppConfig } from '@r-client/shared/util/core';

// TODO: https://linear.app/republic/issue/CLG-16/env-variables-from-aws-secret-manager
// Remove when the above is implemented
const STAGING_ZENDESK_KEY = '437806e4-1dc3-469d-94ad-1437334fb80e';

// Main Component
const GlobalHome = observer(function GlobalHome() {
  const { applicationEnvironment, applicationURLOrigin, zendeskKey } =
    useAppConfig();
  const { viewer } = useAuth();
  const router = useRouter();
  const { region } = useRegionContext();

  const gdprQuery = useGdprQuery({
    lazy: true,
  });

  const isGlobal = region === 'global';
  const pageSeoImage = isGlobal ? defaultCoverImage : usHomeCoverImage;

  useBackendAnalyticsIntegration(!!viewer?.isAuthenticated);

  return (
    <ChakraProvider theme={republicLegacy}>
      <PageSEOMeta
        title="Republic — Invest in Startups, Crypto and More"
        metaTitle="Invest in the world's most ambitious ventures"
        description="On Republic, anyone can invest in startups. Become an investor in cutting-edge private companies with as little as $50 ✅"
        url={toAbsoluteURL(applicationURLOrigin, router.asPath)}
        image={toAbsoluteURL(applicationURLOrigin, pageSeoImage)}
        canonicalUrl="https://republic.com"
      />
      <MainLayout
        theme="new-homepage" // TODO: https://linear.app/republic/issue/USMKT-1457/change-all-hardcoded-colours-for-the-new-hp-to-the-proper-tokens
        footerConfig={{
          disclaimer: (
            <>
              <MainLoggedOut />
              <Main />
            </>
          ),
        }}
        headerConfig={{
          hideRaiseCapitalCta: isGlobal,
          hideEventDisplay: isGlobal,
          // hideBanner: isGlobal, // temp. enabling for mirror tokens launch (revert after banner is switched off)
          hideTradeRepublicDisclosure: !gdprQuery.data?.gdprZone,
        }}
        sticky
      >
        <Head>
          <title>Republic — Invest in Startups, Crypto and More</title>
        </Head>
        <ZendeskScript
          email={viewer?.info?.email}
          firstName={viewer?.info?.firstName}
          lastName={viewer?.info?.lastName}
          zendeskKey={
            applicationEnvironment === 'staging'
              ? STAGING_ZENDESK_KEY
              : zendeskKey
          }
        />
        <ErrorBoundary>
          <PageContents />
        </ErrorBoundary>
      </MainLayout>
    </ChakraProvider>
  );
});

// Page Contents - it uses data from region context so needs be wrapped whole page
const PageContents = observer(function PageContents() {
  const analytics = useAnalytics();
  const { region } = useRegionContext();

  const unify = useUnifyIntent();

  useEffect(() => {
    if (analytics) {
      analytics.page({ name: 'Global Home', options: { region } });
    }
  }, [analytics, region]);

  useEffect(() => {
    // since the global and us component are rendered conditionally and page method doesn't allow us to distinguish between the two
    // we just call the page method here on an initial page load and unmount it to remove the unify instance
    if (unify) {
      unify.page();
    } else {
      return;
    }

    return () => {
      unify.unmount();
    };
  }, [unify]);

  return (
    <>
      <TopSection region={region} />
      <WhatsNew />
      {/*
        US visitors get the /investment-opportunities rails — real-world assets
        and live deals — in place of the US/EU offering grids. `region === 'us'`
        rather than `!== 'global'`, so an explicit europe selection keeps the
        grids with their EU block.
      */}
      {region === 'us' ? (
        <InvestmentOpportunities />
      ) : (
        <Opportunities region={region} />
      )}
      <RepublicNumbers />
      <FounderSpotlights />
      <BusinessCases />
      <Services />
      {region === 'global' ? <SecondaryMarket /> : null}
      <RepublicNote />
      <Regulations />
      <CreateWallet />
      <JoinCommunity />
    </>
  );
});

export default withRegionContext(GlobalHome);
